import React, { useState, useEffect, useCallback, useRef } from 'react';
import { purchaseService, customerService, productService, gatePassService } from '../../services/financeService';
import { buildPurchaseRows } from '../../utils/exportUtils';
import { API_BASE } from '../../utils/apiUtils';
import AttachmentPanel from './AttachmentPanel';
import PaymentModeFields, { PAYMENT_MODES } from './PaymentModeFields';
import { ProductForm } from './ProductManagement';
import SearchableSelect from './SearchableSelect';
import CsvActions from '../common/CsvActions';
import PdfActions from '../common/PdfActions';
import './Financial.css';

const CURRENCIES = ['PKR', 'USD', 'EUR', 'AED', 'GBP'];
const UNITS = ['KG', 'TON', 'BAG', 'CONTAINER', 'PIECE', 'LITER', 'METER', 'BOX'];

const STATUS_STYLE = {
  UNPAID:    { background: 'var(--color-error-light)', color: 'var(--color-error)' },
  PARTIAL:   { background: 'var(--color-warning-light)', color: 'var(--color-warning)' },
  PAID:      { background: 'var(--color-success-light)', color: 'var(--color-success)' },
  CANCELLED: { background: 'var(--color-neutral-100)', color: 'var(--color-text-secondary)' },
};

const badge = (text) => {
  const s = STATUS_STYLE[text] || { background: 'var(--color-neutral-100)', color: 'var(--color-text-secondary)' };
  return (
    <span style={{ ...s, padding: '2px 10px', borderRadius: '9999px', fontSize: '11px', fontWeight: 600 }}>
      {text}
    </span>
  );
};

const fmt = (n) => n != null ? Number(n).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';

const emptyItem = () => ({ productId: '', itemName: '', itemCode: '', description: '', quantity: '', unit: 'KG', unitPrice: '' });

export default function PurchasesManagement() {
  const [activeTab, setActiveTab] = useState('purchases'); // purchases | ledger
  const [purchases, setPurchases] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // create modal
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState(null);

  // detail modal
  const [selected, setSelected] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  // payment modal
  const [payPurchase, setPayPurchase] = useState(null);
  const [payAmount, setPayAmount] = useState('');
  const [payDate, setPayDate] = useState('');
  const [payNotes, setPayNotes] = useState('');
  const [payMode, setPayMode] = useState('CASH');
  const [payDetails, setPayDetails] = useState({});
  const [payLoading, setPayLoading] = useState(false);
  const [payError, setPayError] = useState(null);
  const [payReceipt, setPayReceipt] = useState(null);
  const [payShowDetail, setPayShowDetail] = useState(false);
  const payAttachRef = useRef(null);

  // edit modal
  const [editingId, setEditingId] = useState(null);

  // received gate passes for linking
  const [receivedGatePasses, setReceivedGatePasses] = useState([]);

  // vendor filter
  const [filterVendor, setFilterVendor] = useState('');
  const [products, setProducts] = useState([]);
  const [toast, setToast] = useState(null);
  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };
  const [cancelConfirmId, setCancelConfirmId] = useState(null);
  const [cancelReason, setCancelReason] = useState('');

  // inline "+ New Product" quick-create
  const [quickAddFor, setQuickAddFor] = useState(null); // item index awaiting a newly created product, or null

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [pList, cList, lList, prodRes, gpRes] = await Promise.all([
        purchaseService.getAll(),
        customerService.getAll(),
        purchaseService.getVendorLedger(),
        productService.getAll({ activeOnly: true }),
        gatePassService.getAvailableForPurchase().catch(() => ({ data: [] })),
      ]);
      setPurchases(Array.isArray(pList) ? pList : []);
      const customers = Array.isArray(cList) ? cList : (cList?.data || []);
      setVendors(customers.filter(c => c.customerType === 'VENDOR' || c.customerType === 'BOTH'));
      const prodList = prodRes?.data || prodRes || [];
      setProducts(Array.isArray(prodList) ? prodList : []);
      setLedger(Array.isArray(lList) ? lList : []);
      // Already filtered server-side to RECEIVED gate passes with no existing Purchase Order —
      // prevents picking one that would trigger a duplicate-purchase rejection.
      const gpList = gpRes?.data || gpRes?.content || (Array.isArray(gpRes) ? gpRes : []);
      setReceivedGatePasses(gpList);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({
      vendorId: p.vendorId,
      purchaseDate: p.purchaseDate,
      dueDate: p.dueDate || '',
      currency: p.currency,
      referenceNumber: p.referenceNumber || '',
      vendorInvoiceNumber: p.vendorInvoiceNumber || '',
      notes: p.notes || '',
      serviceCharges: p.serviceCharges || '',
      laborCost: p.laborCost || '',
      updateStock: false,  // items already processed; don't re-update stock
      items: (p.items || []).map(i => ({
        productId: i.productId || '',
        itemName: i.itemName || '',
        itemCode: i.itemCode || '',
        description: i.description || '',
        quantity: i.quantity || '',
        unit: i.unit || 'KG',
        unitPrice: i.unitPrice || '',
      })),
    });
    setFormError(null);
    setShowCreate(true);
  };

  const openCreate = () => {
    setEditingId(null);
    setForm({
      vendorId: '',
      purchaseDate: new Date().toISOString().split('T')[0],
      dueDate: '',
      currency: 'PKR',
      purchaseNumber: '',
      referenceNumber: '',
      vendorInvoiceNumber: '',
      notes: '',
      serviceCharges: '',
      laborCost: '',
      updateStock: true,
      linkedGatePassId: '',
      items: [emptyItem()],
    });
    setFormError(null);
    setShowCreate(true);
    // Auto-fill the PO number from backend suggestion
    purchaseService.suggestNumber().then(n => setForm(f => ({ ...f, purchaseNumber: n }))).catch(() => {});
  };

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const setItem = (idx, k, v) => setForm(f => {
    const items = [...f.items];
    items[idx] = { ...items[idx], [k]: v };
    return { ...f, items };
  });

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, emptyItem()] }));
  const removeItem = (idx) => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const lineTotal = (item) => {
    const q = parseFloat(item.quantity) || 0;
    const p = parseFloat(item.unitPrice) || 0;
    return q * p;
  };

  // Labor/service placeholder products (category CONSUMABLE) are cost entries, not physical
  // stock — they must never be selectable as a purchased good.
  const purchasableProducts = products.filter(p => p.category !== 'CONSUMABLE');

  const handleProductCreated = (itemIndex) => (product) => {
    setProducts(prev => [...prev, product]);
    setItem(itemIndex, 'productId', product.id);
    setItem(itemIndex, 'itemName', product.name);
    setItem(itemIndex, 'itemCode', product.productCode || '');
    setItem(itemIndex, 'unit', product.unit || 'KG');
    setQuickAddFor(null);
  };

  const itemsTotal = form ? form.items.reduce((s, i) => s + lineTotal(i), 0) : 0;
  const grandTotal = itemsTotal + (form ? (parseFloat(form.serviceCharges) || 0) + (parseFloat(form.laborCost) || 0) : 0);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.vendorId) { setFormError('Please select a vendor.'); return; }
    // Gate pass link is mandatory for new purchases
    if (!editingId && !form.linkedGatePassId) {
      setFormError('Please link a Gate Pass. Receive the goods via Gate Pass first, then create the purchase here.');
      return;
    }
    if (!editingId && form.items.some(i => !i.productId)) {
      setFormError('Select a product from the catalog for each item — free text is not allowed to prevent duplicate entries.');
      return;
    }
    if (!editingId && form.items.some(i => !i.quantity || !i.unitPrice)) {
      setFormError('All items must have quantity and unit price.');
      return;
    }
    setFormLoading(true);
    setFormError(null);
    try {
      const payload = {
        ...form,
        vendorId: Number(form.vendorId),
        updateStock: form.updateStock !== false,
        serviceCharges: form.serviceCharges ? parseFloat(form.serviceCharges) : 0,
        laborCost: form.laborCost ? parseFloat(form.laborCost) : 0,
        items: form.items.map(i => ({
          itemName: i.itemName,
          itemCode: i.itemCode || null,
          description: i.description || null,
          quantity: parseFloat(i.quantity),
          unit: i.unit,
          unitPrice: parseFloat(i.unitPrice),
          productId: i.productId ? Number(i.productId) : null,
        })),
      };
      if (editingId) {
        await purchaseService.update(editingId, payload);
        showToast('Purchase updated successfully');
      } else {
        await purchaseService.create(payload);
        const stockCount = form.items.filter(i => i.productId && form.updateStock).length;
        showToast(`Purchase saved${stockCount > 0 ? ` — ${stockCount} product(s) stock updated` : ' — no stock update (manual mode)'}`);
      }
      setShowCreate(false);
      setEditingId(null);
      loadAll();
    } catch (e) {
      setFormError(e.message);
    } finally {
      setFormLoading(false);
    }
  };

  const openDetail = (p) => { setSelected(p); setShowDetail(true); };

  // Fetches the purchase PDF blob — used by <PdfActions> for Preview + Download, added
  // 2026-09-14 so it can be checked before committing to a file.
  const fetchPurchasePdfBlob = async (purchase) => {
    const response = await fetch(`${API_BASE}/purchases/${purchase.id}/pdf`, {
      method: 'GET', credentials: 'include', headers: { Accept: 'application/pdf' }
    });
    if (!response.ok) throw new Error('Failed to load PDF');
    return response.blob();
  };

  const openPayment = (p) => {
    setPayPurchase(p);
    setPayAmount('');
    setPayDate(new Date().toISOString().split('T')[0]);
    setPayNotes('');
    setPayMode('CASH');
    setPayDetails({});
    setPayError(null);
    setPayReceipt(null);
    setPayShowDetail(false);
  };

  const handlePayment = async () => {
    const amount = parseFloat(payAmount);
    const outstanding = parseFloat(payPurchase.outstandingAmount);
    if (!amount || amount <= 0) { setPayError('Enter a valid amount.'); return; }
    if (amount > outstanding) { setPayError(`Amount exceeds outstanding (${fmt(outstanding)} ${payPurchase.currency}).`); return; }
    setPayLoading(true);
    setPayError(null);
    try {
      const res = await purchaseService.recordPayment(payPurchase.id, amount, payNotes, payDate, payMode, payDetails);
      const cashTxnId = res?.lastPaymentCashTransactionId || res?.data?.lastPaymentCashTransactionId;
      if (cashTxnId && payAttachRef.current?.hasPending()) {
        await payAttachRef.current.uploadPending('CASH', cashTxnId);
      }
      // Show a plain-language receipt instead of just a toast — the ledger entry was posted
      // correctly the whole time, but nothing ever confirmed that back to the user.
      setPayReceipt({
        amount,
        modeLabel: PAYMENT_MODES.find(m => m.value === payMode)?.label || payMode,
        vendorName: payPurchase.vendorName,
        currency: payPurchase.currency || 'PKR',
        newOutstanding: res?.outstandingAmount ?? res?.data?.outstandingAmount,
        newStatus: res?.status || res?.data?.status,
      });
    } catch (e) {
      setPayError(e.message);
    } finally {
      setPayLoading(false);
    }
  };

  const closePaymentDone = () => { setPayReceipt(null); setPayPurchase(null); loadAll(); };

  const handleCancel = (id) => { setCancelConfirmId(id); setCancelReason(''); };

  const doCancel = async () => {
    const id = cancelConfirmId;
    const reason = cancelReason;
    setCancelConfirmId(null);
    setCancelReason('');
    try {
      await purchaseService.cancel(id, reason);
      showToast('Purchase cancelled');
      loadAll();
    } catch (e) {
      showToast(e.message, 'error');
    }
  };

  const displayed = filterVendor
    ? purchases.filter(p => String(p.vendorId) === String(filterVendor))
    : purchases;

  return (
    <div className="financial-container">
      {/* Header */}
      <div className="financial-header">
        <div>
          <h2 className="financial-title">Purchases & Vendor Ledger</h2>
          <p className="financial-subtitle">Track vendor purchases, payments, and outstanding balances</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          {(() => { const { headers, matrix } = buildPurchaseRows(purchases); return (
            <CsvActions filename="purchases" headers={headers} matrix={matrix} />
          ); })()}
          <button className="btn-primary" onClick={openCreate}>+ New Purchase</button>
        </div>
      </div>

      {error && (
        <div style={{ background: 'var(--color-error-light)', color: 'var(--color-error)', padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 14 }}>
          {error}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', borderBottom: '2px solid var(--color-border-medium)' }}>
        {[['purchases', 'All Purchases'], ['ledger', 'Vendor Ledger']].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            style={{
              padding: '8px 20px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontWeight: activeTab === key ? 700 : 400,
              color: activeTab === key ? 'var(--color-primary)' : 'var(--color-text-secondary)',
              borderBottom: activeTab === key ? '2px solid var(--color-primary)' : '2px solid transparent',
              marginBottom: '-2px',
              fontSize: '14px',
            }}
          >
            {label}
            {key === 'purchases' && <span style={{ marginLeft: '6px', background: 'var(--color-border-medium)', borderRadius: '9999px', padding: '1px 8px', fontSize: '11px' }}>{purchases.length}</span>}
            {key === 'ledger' && <span style={{ marginLeft: '6px', background: 'var(--color-border-medium)', borderRadius: '9999px', padding: '1px 8px', fontSize: '11px' }}>{ledger.length}</span>}
          </button>
        ))}
      </div>

      {/* Purchases Tab */}
      {activeTab === 'purchases' && (
        <>
          {/* Filters */}
          <div style={{ display: 'flex', gap: '10px', marginBottom: '14px', flexWrap: 'wrap' }}>
            <select
              className="form-control"
              style={{ width: '220px' }}
              value={filterVendor}
              onChange={e => setFilterVendor(e.target.value)}
            >
              <option value="">All Vendors</option>
              {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
            {filterVendor && (
              <button className="btn-secondary" onClick={() => setFilterVendor('')}>Clear Filter</button>
            )}
          </div>

          {loading ? (
            <div className="loading-state">Loading…</div>
          ) : (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>PO #</th>
                    <th>Vendor</th>
                    <th>Date</th>
                    <th>Due Date</th>
                    <th>Currency</th>
                    <th>Bill Amount</th>
                    <th>Amount Paid</th>
                    <th>Balance Due</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {displayed.length === 0 ? (
                    <tr><td colSpan={10} style={{ textAlign: 'center', color: 'var(--color-text-tertiary)', padding: '32px' }}>No purchases found.</td></tr>
                  ) : displayed.map(p => (
                    <tr key={p.id}>
                      <td style={{ fontWeight: 600, color: 'var(--color-primary)', cursor: 'pointer' }} onClick={() => openDetail(p)}>{p.purchaseNumber}</td>
                      <td style={{ minWidth: 180 }}>{p.vendorName}</td>
                      <td className="nowrap">{p.purchaseDate}</td>
                      <td className="nowrap" style={{ color: p.dueDate && new Date(p.dueDate) < new Date() && p.status !== 'PAID' ? 'var(--color-error)' : undefined }}>{p.dueDate || '—'}</td>
                      <td>{p.currency}</td>
                      <td style={{ textAlign: 'right' }}>{fmt(p.totalAmount)}</td>
                      <td style={{ textAlign: 'right', color: 'var(--color-success)' }}>{fmt(p.paidAmount)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600, color: parseFloat(p.outstandingAmount) > 0 ? 'var(--color-error)' : 'var(--color-success)' }}>{fmt(p.outstandingAmount)}</td>
                      <td>
                        {badge(p.status)}
                        {p.status === 'CANCELLED' && p.cancelReason && (
                          <div style={{ fontSize: '11px', color: 'var(--color-text-secondary)', marginTop: '3px', maxWidth: '160px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={p.cancelReason}>
                            {p.cancelReason}
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button className="action-btn btn-secondary" onClick={() => openDetail(p)}>View</button>
                          {p.status !== 'PAID' && p.status !== 'CANCELLED' && (
                            <button className="action-btn btn-secondary" onClick={() => openEdit(p)}>Edit</button>
                          )}
                          {p.status !== 'PAID' && p.status !== 'CANCELLED' && (
                            <button className="action-btn btn-primary" onClick={() => openPayment(p)}>Pay</button>
                          )}
                          {p.status !== 'CANCELLED' && p.status !== 'PAID' && (
                            <button className="action-btn btn-danger" onClick={() => handleCancel(p.id)}>Cancel</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Vendor Ledger Tab */}
      {activeTab === 'ledger' && (
        <>
          {/* Summary cards */}
          <div style={{ display: 'flex', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' }}>
            {[
              { label: 'Total Vendors', value: ledger.length, color: 'var(--color-primary)' },
              { label: 'Total Purchased', value: fmt(ledger.reduce((s, v) => s + parseFloat(v.totalPurchased || 0), 0)), color: 'var(--color-text-secondary)' },
              { label: 'Total Paid', value: fmt(ledger.reduce((s, v) => s + parseFloat(v.totalPaid || 0), 0)), color: 'var(--color-success)' },
              { label: 'Total Outstanding', value: fmt(ledger.reduce((s, v) => s + parseFloat(v.outstanding || 0), 0)), color: 'var(--color-error)' },
            ].map(card => (
              <div key={card.label} style={{ background: 'var(--color-bg-primary)', border: '1px solid var(--color-border-medium)', borderRadius: '10px', padding: '14px 20px', flex: '1', minWidth: '160px' }}>
                <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>{card.label}</div>
                <div style={{ fontSize: '20px', fontWeight: 700, color: card.color, marginTop: '4px' }}>{card.value}</div>
              </div>
            ))}
          </div>

          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Vendor</th>
                  <th style={{ textAlign: 'right' }}>Purchases (#)</th>
                  <th style={{ textAlign: 'right' }}>Total Purchased</th>
                  <th style={{ textAlign: 'right' }}>Total Paid</th>
                  <th style={{ textAlign: 'right' }}>Balance Due</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {ledger.length === 0 ? (
                  <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--color-text-tertiary)', padding: '32px' }}>No vendor data. Create purchases first.</td></tr>
                ) : ledger.map(v => (
                  <tr key={v.vendorId} style={{ background: parseFloat(v.outstanding) > 0 ? 'var(--color-error-light)' : undefined }}>
                    <td style={{ fontWeight: 600 }}>{v.vendorName}</td>
                    <td style={{ textAlign: 'right' }}>{v.purchaseCount}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(v.totalPurchased)}</td>
                    <td style={{ textAlign: 'right', color: 'var(--color-success)' }}>{fmt(v.totalPaid)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: parseFloat(v.outstanding) > 0 ? 'var(--color-error)' : 'var(--color-success)' }}>{fmt(v.outstanding)}</td>
                    <td>
                      <button
                        className="btn-secondary"
                        style={{ padding: '3px 10px', fontSize: '12px' }}
                        onClick={() => { setFilterVendor(String(v.vendorId)); setActiveTab('purchases'); }}
                      >
                        View Orders
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ── Create Purchase Modal ── */}
      {showCreate && form && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="modal-content" style={{ maxWidth: '760px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingId ? 'Edit Purchase Order' : 'New Purchase Order'}</h3>
              <button className="modal-close" onClick={() => { setShowCreate(false); setEditingId(null); }}>✕</button>
            </div>
            <form onSubmit={handleCreate}>
              <div className="modal-body">
                {formError && <div style={{ background: 'var(--color-error-light)', color: 'var(--color-error)', padding: '10px 16px', borderRadius: 8, marginBottom: 12, fontSize: 14 }}>{formError}</div>}

                {/* ── STEP 1: Gate Pass (always first) ── */}
                {!editingId && (
                  <div style={{ background: form.linkedGatePassId ? 'var(--color-success-light)' : 'var(--color-warning-light)', border: `2px solid ${form.linkedGatePassId ? 'var(--color-success)' : 'var(--color-warning)'}`, borderRadius: 10, padding: '14px 16px', marginBottom: 16 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8, color: form.linkedGatePassId ? 'var(--color-success)' : 'var(--color-warning)' }}>
                      {form.linkedGatePassId ? '✓ Step 1 — Gate pass selected' : 'Step 1 — Select a received gate pass (required)'}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 10 }}>
                      A Purchase is the <strong>financial record</strong> of goods already received via Gate Pass.
                      Select the gate pass → vendor, items and quantities fill automatically.
                    </div>
                    {receivedGatePasses.length === 0 ? (
                      <div style={{ background: 'var(--color-error-light)', border: '1px solid var(--color-error)', borderRadius: 6, padding: '10px 14px', fontSize: 13, color: 'var(--color-error)' }}>
                        No received gate passes found. You must <strong>Go to Gate Passes → ENTRY → click Receive</strong> on a pending gate pass first, then come back here.
                      </div>
                    ) : (
                      <select
                        className="form-control"
                        required
                        value={form.linkedGatePassId || ''}
                        style={{ borderColor: !form.linkedGatePassId ? 'var(--color-warning)' : 'var(--color-success)', fontWeight: 500 }}
                        onChange={async e => {
                          const gpId = e.target.value;
                          setField('linkedGatePassId', gpId || null);
                          if (gpId) {
                            const gp = receivedGatePasses.find(g => String(g.id) === gpId);
                            if (gp?.partyName) {
                              const matchedVendor = vendors.find(v => v.name === gp.partyName);
                              if (matchedVendor) setField('vendorId', matchedVendor.id);
                            }
                            // Auto-fill purchase date from gate pass date
                            const gpDate = gp?.gatePassDate || gp?.createdAt?.split('T')[0];
                            if (gpDate) setField('purchaseDate', gpDate);
                            setField('updateStock', false);
                            try {
                              const detail = await gatePassService.getById(Number(gpId));
                              const gpItems = detail?.data?.items || detail?.items || [];
                              if (gpItems.length > 0) {
                                const prefilled = gpItems.map(it => ({
                                  productId: it.productId ? Number(it.productId) : '',
                                  itemName: it.itemName || '',
                                  itemCode: it.itemCode || '',
                                  description: '',
                                  quantity: it.weight && parseFloat(it.weight) > 0 ? it.weight : it.quantity || '',
                                  unit: it.unit || 'KG',
                                  unitPrice: it.purchasePrice || '',
                                }));
                                setForm(f => ({ ...f, items: prefilled }));
                              }
                            } catch (_) {}
                          } else {
                            setField('updateStock', true);
                            setForm(f => ({ ...f, items: [emptyItem()] }));
                          }
                        }}
                      >
                        <option value="">— Select Gate Pass —</option>
                        {receivedGatePasses.map(gp => (
                          <option key={gp.id} value={gp.id}>
                            {gp.gatePassNumber} — {gp.partyName || 'Unknown party'} — {gp.gatePassDate || gp.createdAt?.split('T')[0] || ''}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                )}

                {editingId && (
                  <div style={{ background: 'var(--color-warning-light)', border: '1px solid var(--color-warning)', borderRadius: '8px', padding: '10px 14px', marginBottom: 14, fontSize: '13px', color: 'var(--color-warning)' }}>
                    <strong>Editing header fields only.</strong> Items and stock have already been processed. To change items, cancel this purchase and create a new one.
                  </div>
                )}

                {/* ── STEP 2: Payment Details (grayed out until gate pass selected) ── */}
                <div style={{ opacity: (!editingId && !form.linkedGatePassId) ? 0.45 : 1, pointerEvents: (!editingId && !form.linkedGatePassId) ? 'none' : undefined }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 10, paddingBottom: 6, borderBottom: '1px solid var(--color-border-medium)' }}>
                    {editingId ? 'Purchase Details' : 'Step 2 — Payment Details'}
                  </div>

                {!editingId && (
                  <div className="form-group" style={{ marginBottom: 14, background: 'var(--color-info-light)', border: '1px solid var(--color-info)', borderRadius: 8, padding: '10px 14px' }}>
                    <label className="form-label" style={{ color: 'var(--color-primary)' }}>
                      PO Number
                      <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--color-text-secondary)', marginLeft: 8 }}>auto-generated · editable if needed</span>
                    </label>
                    <input
                      className="form-control"
                      value={form.purchaseNumber || ''}
                      onChange={e => setField('purchaseNumber', e.target.value)}
                      placeholder="Generating…"
                      style={{ fontWeight: 600, letterSpacing: '0.03em', fontFamily: 'var(--font-mono)' }}
                    />
                  </div>
                )}

                <div className="form-grid-2">
                  <div className="form-group">
                    <label className="form-label">Vendor *
                      {form.linkedGatePassId && <span style={{ fontSize: 11, color: 'var(--color-success)', fontWeight: 400, marginLeft: 6 }}>auto-filled from gate pass</span>}
                    </label>
                    <SearchableSelect
                      required
                      placeholder="— Select Vendor —"
                      value={form.vendorId}
                      options={vendors.map(v => ({ value: v.id, label: v.name }))}
                      onChange={(value) => setField('vendorId', value)}
                    />
                    {vendors.length === 0 && (
                      <small style={{ color: 'var(--color-warning)' }}>No vendors found. Add a customer with type VENDOR or BOTH first.</small>
                    )}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Currency</label>
                    <select className="form-control" value={form.currency} onChange={e => setField('currency', e.target.value)}>
                      {CURRENCIES.map(c => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Purchase Date *</label>
                    <input type="date" className="form-control" required value={form.purchaseDate} onChange={e => setField('purchaseDate', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Due Date</label>
                    <input type="date" className="form-control" value={form.dueDate} onChange={e => setField('dueDate', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Our Reference #</label>
                    <input className="form-control" placeholder="Internal PO ref" value={form.referenceNumber} onChange={e => setField('referenceNumber', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Vendor Invoice #</label>
                    <input className="form-control" placeholder="Vendor's invoice number" value={form.vendorInvoiceNumber} onChange={e => setField('vendorInvoiceNumber', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Service Charges ({form.currency})
                      <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--color-text-secondary)', marginLeft: 6 }}>e.g. mandi commission, agent fee</span>
                    </label>
                    <input type="number" className="form-control" min="0" step="0.01" placeholder="0.00" value={form.serviceCharges} onChange={e => setField('serviceCharges', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Receiving Labor ({form.currency})
                      <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--color-text-secondary)', marginLeft: 6 }}>e.g. mandi loading/unloading</span>
                    </label>
                    <input type="number" className="form-control" min="0" step="0.01" placeholder="0.00" value={form.laborCost} onChange={e => setField('laborCost', e.target.value)} />
                  </div>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label className="form-label">Notes</label>
                    <textarea className="form-control" rows={2} value={form.notes} onChange={e => setField('notes', e.target.value)} />
                  </div>

                </div>

                {/* ── STEP 3: Items (auto-filled, read-only product, editable qty/price) ── */}
                <div style={{ marginTop: '16px', display: editingId ? 'none' : undefined, opacity: (!editingId && !form.linkedGatePassId) ? 0.45 : 1, pointerEvents: (!editingId && !form.linkedGatePassId) ? 'none' : undefined }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <div>
                      <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--color-text-secondary)' }}>Step 3 — Items</span>
                      {form.linkedGatePassId
                        ? <span style={{ fontSize: 11, color: 'var(--color-success)', marginLeft: 8 }}>auto-filled from gate pass — verify qty & price</span>
                        : <span style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginLeft: 8 }}>select gate pass above first</span>}
                    </div>
                    {form.linkedGatePassId && (
                      <button type="button" className="btn-secondary" style={{ padding: '4px 12px', fontSize: '13px' }} onClick={addItem}>+ Add Item</button>
                    )}
                  </div>

                  {form.items.map((item, i) => (
                    <div key={i} style={{ background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-medium)', borderRadius: '8px', padding: '12px', marginBottom: '10px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span style={{ fontWeight: 600, fontSize: '13px', color: 'var(--color-text-secondary)' }}>Item {i + 1}</span>
                        {form.items.length > 1 && (
                          <button type="button" style={{ background: 'none', border: 'none', color: 'var(--color-error)', cursor: 'pointer', fontSize: '13px' }} onClick={() => removeItem(i)}>✕ Remove</button>
                        )}
                      </div>

                      <div className="form-grid-2">
                        <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                          <label className="form-label" style={{ color: !item.productId ? 'var(--color-error)' : undefined }}>
                            Product *
                            {item.productId
                              ? <span style={{ marginLeft: 8, color: 'var(--color-success)', fontSize: 11, fontWeight: 400 }}>✓ linked — {item.itemName}</span>
                              : <span style={{ marginLeft: 8, color: 'var(--color-error)', fontSize: 11, fontWeight: 400 }}>required — select from catalog, no free text</span>}
                          </label>
                          <div style={{ display: 'flex', gap: 8 }}>
                            {purchasableProducts.length === 0 ? (
                              <div style={{ flex: 1, padding: '7px 10px', border: '1px solid var(--color-error)', borderRadius: 6, fontSize: 13, background: 'var(--color-error-light)', color: 'var(--color-error)' }}>
                                No products in catalog yet.
                              </div>
                            ) : (
                              <SearchableSelect
                                required
                                placeholder="— Select Product —"
                                style={{ flex: 1 }}
                                value={item.productId || ''}
                                options={purchasableProducts.map(p => ({
                                  value: p.id,
                                  label: `${p.name}${p.productCode ? ` [${p.productCode}]` : ''} — Stock: ${p.currentStock ?? 0} ${p.unit}`,
                                }))}
                                onChange={(value) => {
                                  const pid = value;
                                  const prod = purchasableProducts.find(p => String(p.id) === String(pid));
                                  setItem(i, 'productId', pid ? Number(pid) : null);
                                  if (prod) {
                                    setItem(i, 'itemName', prod.name);
                                    setItem(i, 'itemCode', prod.productCode || '');
                                    setItem(i, 'unit', prod.unit || 'KG');
                                  }
                                }}
                              />
                            )}
                            <button type="button" className="btn-secondary" style={{ whiteSpace: 'nowrap', padding: '4px 12px' }} onClick={() => setQuickAddFor(i)}>
                              + New Product
                            </button>
                          </div>
                        </div>
                        <div className="form-group">
                          <label className="form-label">Item Name</label>
                          <input className="form-control" readOnly style={{ background: 'var(--color-neutral-100)' }} placeholder="Auto-filled from product" value={item.itemName} />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Item Code</label>
                          <input className="form-control" readOnly style={{ background: 'var(--color-neutral-100)' }} placeholder="Auto-filled" value={item.itemCode} />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Quantity *</label>
                          <input type="number" className="form-control" required min="0.001" step="0.001" placeholder="0" value={item.quantity} onChange={e => setItem(i, 'quantity', e.target.value)} />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Unit *</label>
                          <select className="form-control" value={item.unit} onChange={e => setItem(i, 'unit', e.target.value)}>
                            {UNITS.map(u => <option key={u}>{u}</option>)}
                          </select>
                        </div>
                        <div className="form-group">
                          <label className="form-label">Unit Price ({form.currency}) *</label>
                          <input type="number" className="form-control" required min="0" step="0.01" placeholder="0.00" value={item.unitPrice} onChange={e => setItem(i, 'unitPrice', e.target.value)} />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Line Total</label>
                          <input className="form-control" readOnly value={fmt(lineTotal(item))} style={{ background: 'var(--color-neutral-100)', fontWeight: 600 }} />
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Stock update — hidden when gate pass linked (stock already came from gate pass) */}
                  {!form.linkedGatePassId && (
                    <div style={{ background: 'var(--color-info-light)', border: '1px solid var(--color-info)', borderRadius: '8px', padding: '12px', marginTop: '10px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                      <input
                        type="checkbox"
                        id="updateStock"
                        checked={form.updateStock !== false}
                        onChange={e => setField('updateStock', e.target.checked)}
                        style={{ marginTop: '3px', cursor: 'pointer', width: '16px', height: '16px' }}
                      />
                      <label htmlFor="updateStock" style={{ fontSize: '13px', cursor: 'pointer', lineHeight: '1.5' }}>
                        <strong>Auto-update stock on save</strong>
                        <br />
                        <span style={{ color: 'var(--color-text-secondary)', fontSize: '12px' }}>
                          Will add stock for {form.items.filter(it => it.productId).length} linked product(s).
                        </span>
                      </label>
                    </div>
                  )}
                  {form.linkedGatePassId && (
                    <div style={{ background: 'var(--color-success-light)', border: '1px solid var(--color-success)', borderRadius: 8, padding: '10px 14px', marginTop: 10, fontSize: 12, color: 'var(--color-success)' }}>
                      Stock was already updated when the gate pass was received — no double-counting will occur.
                    </div>
                  )}

                  <div style={{ textAlign: 'right', fontWeight: 700, fontSize: '16px', padding: '10px 0', borderTop: '2px solid var(--color-border-medium)', marginTop: '12px' }}>
                    Grand Total: {form.currency} {fmt(grandTotal)}
                  </div>
                </div>
                </div>{/* end step 2 wrapper */}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-secondary" disabled={formLoading} onClick={() => setShowCreate(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={formLoading}>
                  {formLoading ? (editingId ? 'Saving…' : 'Creating…') : (editingId ? 'Save Changes' : 'Create Purchase')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Detail Modal ── */}
      {showDetail && selected && (
        <div className="modal-overlay" onClick={() => setShowDetail(false)}>
          <div className="modal-content" style={{ maxWidth: '680px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{selected.purchaseNumber} <span style={{ marginLeft: '8px' }}>{badge(selected.status)}</span></h3>
              <button className="modal-close" onClick={() => setShowDetail(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-grid-2" style={{ marginBottom: '16px' }}>
                {[
                  ['Vendor', selected.vendorName],
                  ['Date', selected.purchaseDate],
                  ['Due Date', selected.dueDate || '—'],
                  ['Currency', selected.currency],
                  ['Our Reference', selected.referenceNumber || '—'],
                  ['Vendor Invoice #', selected.vendorInvoiceNumber || '—'],
                  ['Linked Gate Pass', selected.linkedGatePassNumber || '—'],
                ].map(([label, value]) => (
                  <div key={label}>
                    <div style={{ fontSize: '11px', color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
                    <div style={{ fontWeight: 500, fontSize: '14px', marginTop: '2px' }}>{value}</div>
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', gap: '16px', background: 'var(--color-bg-secondary)', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
                <div style={{ flex: 1, textAlign: 'center' }}>
                  <div style={{ fontSize: '11px', color: 'var(--color-text-secondary)' }}>Bill Amount</div>
                  <div style={{ fontWeight: 700, fontSize: '18px' }}>{selected.currency} {fmt(selected.totalAmount)}</div>
                </div>
                <div style={{ flex: 1, textAlign: 'center' }}>
                  <div style={{ fontSize: '11px', color: 'var(--color-text-secondary)' }}>Amount Paid</div>
                  <div style={{ fontWeight: 700, fontSize: '18px', color: 'var(--color-success)' }}>{selected.currency} {fmt(selected.paidAmount)}</div>
                </div>
                <div style={{ flex: 1, textAlign: 'center' }}>
                  <div style={{ fontSize: '11px', color: 'var(--color-text-secondary)' }}>Balance Due</div>
                  <div style={{ fontWeight: 700, fontSize: '18px', color: parseFloat(selected.outstandingAmount) > 0 ? 'var(--color-error)' : 'var(--color-success)' }}>{selected.currency} {fmt(selected.outstandingAmount)}</div>
                </div>
              </div>

              {selected.notes && (
                <div style={{ background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-medium)', borderRadius: '8px', padding: '10px 14px', marginBottom: '14px', fontSize: '13px' }}>
                  <strong>Notes:</strong> {selected.notes}
                </div>
              )}
              {selected.cancelReason && (
                <div style={{ background: 'var(--color-error-light)', border: '1px solid var(--color-error)', borderRadius: '8px', padding: '10px 14px', marginBottom: '14px', fontSize: '13px', color: 'var(--color-error)' }}>
                  <strong>Cancel Reason:</strong> {selected.cancelReason}
                </div>
              )}

              <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '10px', color: 'var(--color-text-secondary)' }}>Items ({selected.items?.length || 0})</h4>
              <div className="table-container">
                <table className="data-table" style={{ fontSize: '12px' }}>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Item</th>
                      <th>Code</th>
                      <th>Qty</th>
                      <th>Unit</th>
                      <th style={{ textAlign: 'right' }}>Unit Price</th>
                      <th style={{ textAlign: 'right' }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selected.items || []).map((item, i) => (
                      <tr key={item.id}>
                        <td>{i + 1}</td>
                        <td style={{ fontWeight: 500 }}>{item.itemName}</td>
                        <td>{item.itemCode || '—'}</td>
                        <td>{item.quantity}</td>
                        <td>{item.unit}</td>
                        <td style={{ textAlign: 'right' }}>{fmt(item.unitPrice)}</td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmt(item.totalPrice)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    {parseFloat(selected.serviceCharges) > 0 && (
                      <tr>
                        <td colSpan={6} style={{ textAlign: 'right', color: 'var(--color-text-secondary)' }}>Service Charges:</td>
                        <td style={{ textAlign: 'right', color: 'var(--color-text-secondary)' }}>{selected.currency} {fmt(selected.serviceCharges)}</td>
                      </tr>
                    )}
                    {parseFloat(selected.laborCost) > 0 && (
                      <tr>
                        <td colSpan={6} style={{ textAlign: 'right', color: 'var(--color-text-secondary)' }}>Receiving Labor:</td>
                        <td style={{ textAlign: 'right', color: 'var(--color-text-secondary)' }}>{selected.currency} {fmt(selected.laborCost)}</td>
                      </tr>
                    )}
                    <tr>
                      <td colSpan={6} style={{ textAlign: 'right', fontWeight: 700 }}>Grand Total:</td>
                      <td style={{ textAlign: 'right', fontWeight: 700 }}>{selected.currency} {fmt(selected.totalAmount)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Payment History */}
              {selected.payments && selected.payments.length > 0 && (
                <div style={{ marginTop: '20px', borderTop: '1px solid var(--color-border-medium)', paddingTop: '16px' }}>
                  <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '10px', color: 'var(--color-text-secondary)' }}>
                    Payment History ({selected.payments.length})
                  </h4>
                  <div className="table-container">
                    <table className="data-table" style={{ fontSize: '12px' }}>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Date</th>
                          <th style={{ textAlign: 'right' }}>Amount</th>
                          <th>Mode</th>
                          <th>Notes</th>
                          <th>Recorded By</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selected.payments.map((pay, i) => (
                          <tr key={pay.id}>
                            <td style={{ color: 'var(--color-text-tertiary)' }}>{i + 1}</td>
                            <td>{pay.paymentDate || '—'}</td>
                            <td style={{ textAlign: 'right', fontWeight: 600, color: 'var(--color-success)' }}>
                              {selected.currency} {fmt(pay.amount)}
                            </td>
                            <td style={{ color: 'var(--color-text-secondary)' }}>
                              {pay.paymentMode ? (PAYMENT_MODES.find(m => m.value === pay.paymentMode)?.label || pay.paymentMode) : '—'}
                            </td>
                            <td style={{ color: 'var(--color-text-secondary)' }}>{pay.notes || '—'}</td>
                            <td style={{ color: 'var(--color-text-secondary)' }}>{pay.createdByName || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colSpan={2} style={{ textAlign: 'right', fontWeight: 700, fontSize: '13px' }}>Total Paid:</td>
                          <td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--color-success)', fontSize: '13px' }}>
                            {selected.currency} {fmt(selected.paidAmount)}
                          </td>
                          <td colSpan={2} />
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}

              {/* Attachments (vendor invoices, delivery receipts, etc.) */}
              <div style={{ marginTop: '20px', borderTop: '1px solid var(--color-border-medium)', paddingTop: '16px' }}>
                <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '10px', color: 'var(--color-text-secondary)' }}>
                  Attachments <span style={{ fontWeight: 400, fontSize: '12px', color: 'var(--color-text-secondary)' }}>(vendor invoices, receipts, etc.)</span>
                </h4>
                <AttachmentPanel entityType="PURCHASE" entityId={selected.id} />
              </div>
            </div>
            <div className="modal-footer">
              {selected.status !== 'PAID' && selected.status !== 'CANCELLED' && (
                <button className="btn-primary" onClick={() => { setShowDetail(false); openPayment(selected); }}>Record Payment</button>
              )}
              <PdfActions
                fetchBlob={() => fetchPurchasePdfBlob(selected)}
                filename={`${selected.purchaseNumber}.pdf`}
                label="PDF"
                className="btn-secondary"
              />
              <button className="btn-secondary" onClick={() => setShowDetail(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Quick Add Product Modal (from within an item row) ── */}
      {quickAddFor !== null && (
        <ProductForm
          vendors={vendors}
          excludeCategories={['CONSUMABLE']}
          onClose={() => setQuickAddFor(null)}
          onSave={handleProductCreated(quickAddFor)}
        />
      )}

      {/* ── Payment Modal ── */}
      {payPurchase && payReceipt && (
        <div className="modal-overlay" onClick={closePaymentDone}>
          <div className="modal-content" style={{ maxWidth: '420px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-body" style={{ padding: '24px 20px', textAlign: 'center' }}>
              <div className="notice-mark notice-mark-success" aria-hidden="true">✓</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--color-success)', marginBottom: 4 }}>Payment Recorded</div>
              <p style={{ color: 'var(--color-text-secondary)', fontSize: 14, margin: '10px 0' }}>
                <strong>{payReceipt.currency} {fmt(payReceipt.amount)}</strong> paid to <strong>{payReceipt.vendorName}</strong> via {payReceipt.modeLabel}
              </p>
              <div style={{ background: 'var(--color-error-light)', border: '1px solid var(--color-error)', borderRadius: 8, padding: '12px 16px', margin: '12px 0', textAlign: 'left' }}>
                <div style={{ fontSize: 12, color: 'var(--color-error)' }}>Outstanding balance now</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: Number(payReceipt.newOutstanding) > 0 ? 'var(--color-error)' : 'var(--color-success)' }}>
                  {payReceipt.currency} {fmt(payReceipt.newOutstanding)}
                </div>
                {payReceipt.newStatus === 'PAID' && <div style={{ fontSize: 12, color: 'var(--color-success)', marginTop: 2, fontWeight: 600 }}>Fully paid — purchase closed</div>}
              </div>
              <button
                onClick={() => setPayShowDetail(s => !s)}
                style={{ background: 'none', border: 'none', color: 'var(--color-text-secondary)', fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}
              >
                {payShowDetail ? 'Hide' : 'View'} accounting detail
              </button>
              {payShowDetail && (
                <div style={{ marginTop: 10, textAlign: 'left', fontSize: 12, color: 'var(--color-text-secondary)', background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-medium)', borderRadius: 8, padding: 10 }}>
                  <div>• Cash decreased by {payReceipt.currency} {fmt(payReceipt.amount)}</div>
                  <div>• Amount you owe {payReceipt.vendorName} decreased by {payReceipt.currency} {fmt(payReceipt.amount)}</div>
                  <div style={{ color: 'var(--color-text-tertiary)', marginTop: 4 }}>Posted to Chart of Accounts — see Payments & Cash for the full entry.</div>
                </div>
              )}
            </div>
            <div className="modal-footer" style={{ justifyContent: 'center' }}>
              <button className="btn-primary" onClick={closePaymentDone}>Done</button>
            </div>
          </div>
        </div>
      )}
      {payPurchase && !payReceipt && (
        <div className="modal-overlay" onClick={() => setPayPurchase(null)}>
          <div className="modal-content" style={{ maxWidth: '560px', maxHeight: '88vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Record Payment — {payPurchase.purchaseNumber}</h3>
              <button className="modal-close" onClick={() => setPayPurchase(null)}>✕</button>
            </div>
            <div className="modal-body">
              {payError && <div style={{ background: 'var(--color-error-light)', color: 'var(--color-error)', padding: '10px 16px', borderRadius: 8, marginBottom: 12, fontSize: 14 }}>{payError}</div>}
              <div style={{ marginBottom: '14px', background: 'var(--color-bg-secondary)', borderRadius: '8px', padding: '12px', display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '13px', color: 'var(--color-text-secondary)' }}>Outstanding Balance:</span>
                <span style={{ fontWeight: 700, color: 'var(--color-error)' }}>{payPurchase.currency} {fmt(payPurchase.outstandingAmount)}</span>
              </div>
              <div className="form-group">
                <label className="form-label">Payment Amount ({payPurchase.currency}) *</label>
                <input
                  type="number"
                  className="form-control"
                  min="0.01"
                  step="0.01"
                  max={payPurchase.outstandingAmount}
                  placeholder="0.00"
                  value={payAmount}
                  onChange={e => setPayAmount(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label className="form-label">Payment Date *</label>
                <input type="date" className="form-control" value={payDate} onChange={e => setPayDate(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Payment Mode</label>
                <select className="form-control" value={payMode} onChange={e => { setPayMode(e.target.value); setPayDetails({}); }}>
                  {PAYMENT_MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </div>
              <PaymentModeFields mode={payMode} details={payDetails} onChange={setPayDetails} />
              <div className="form-group">
                <label className="form-label">Narration / Notes</label>
                <input className="form-control" placeholder="Any additional notes" value={payNotes} onChange={e => setPayNotes(e.target.value)} />
              </div>
              <AttachmentPanel ref={payAttachRef} entityType="CASH" entityId={null} />
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setPayPurchase(null)}>Cancel</button>
              <button className="btn-primary" disabled={payLoading} onClick={handlePayment}>
                {payLoading ? 'Saving…' : 'Confirm Payment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Cancel Confirm ── */}
      {cancelConfirmId && (
        <div className="modal-overlay" onClick={() => setCancelConfirmId(null)}>
          <div className="modal-content" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Cancel Purchase</h3>
              <button className="modal-close" onClick={() => setCancelConfirmId(null)}>✕</button>
            </div>
            <div style={{ padding: '4px 0 16px' }}>
              <p style={{ color: 'var(--color-text-secondary)', marginBottom: '14px' }}>
                This will reverse stock for all linked products. Please provide a reason.
              </p>
              <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--color-text-secondary)', display: 'block', marginBottom: '6px' }}>
                Cancel Reason *
              </label>
              <textarea
                className="form-control"
                rows={3}
                placeholder="e.g. Duplicate entry, wrong vendor, goods not received..."
                value={cancelReason}
                onChange={e => setCancelReason(e.target.value)}
                autoFocus
                style={{ resize: 'vertical' }}
              />
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setCancelConfirmId(null)}>No, keep it</button>
              <button className="btn-danger" onClick={doCancel} disabled={!cancelReason.trim()}>Yes, cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Toast ── */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: '24px', right: '24px', zIndex: 9999,
          background: toast.type === 'error' ? 'var(--color-error-light)' : 'var(--color-success-light)',
          color: toast.type === 'error' ? 'var(--color-error)' : 'var(--color-success)',
          border: `1px solid ${toast.type === 'error' ? 'var(--color-error)' : 'var(--color-success)'}`,
          padding: '14px 22px', borderRadius: '10px', fontSize: '14px', fontWeight: 600,
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          display: 'flex', alignItems: 'center', gap: '10px',
        }}>
          <span>{toast.type === 'error' ? '!' : '✓'}</span>
          {toast.msg}
        </div>
      )}
    </div>
  );
}
